import {
    buildApiUrl,
    buildAuthenticatedFetchOptions,
    getResponseStatusCategory,
    RESPONSE_STATUS,
} from '@Sage/ssm-utils';
import { NEW_API_URL } from 'constants/Environment';
import Context from 'managers/Context';
import Cookies from 'js-cookie';
import { goLogout } from 'utils/routes';
import { persistTokens, refreshToken } from 'services';

/**
 * Creates a configured fetch instance with automatic token handling and error management
 * @param {Object} config - Configuration object for the fetch instance
 * @param {string} config.baseURL - Base URL for all API requests
 * @returns {Function} Configured fetch instance with additional methods
 */
const createInstance = (config) => {
    /**
     * Enhanced fetch wrapper with automatic authentication and error handling
     * @param {string} endpoint - API endpoint path (will be appended to baseURL)
     * @param {Object} options - Fetch options
     * @param {string} [options.method='GET'] - HTTP method
     * @param {Object} [options.headers={}] - Additional headers
     * @param {string} [options.token] - Custom authorization token (overrides cookie token)
     * @param {Object|string} [options.body] - Request body
     * @returns {Promise<Response>} Fetch response promise
     */
    const innerInstance = (endpoint, options = {}) => {
        const innerToken = `Bearer ${Cookies.get('token')}`;
        const finalOptions = buildAuthenticatedFetchOptions(options, innerToken);
        const url = buildApiUrl(config.baseURL, endpoint);

        return fetch(url, finalOptions)
            .then((response) => {
                const statusCategory = getResponseStatusCategory(response);
                if (statusCategory === RESPONSE_STATUS.UNAUTHORIZED) {
                    // Token expired - attempt to refresh using stored refresh token
                    return refreshToken(Context.config?.refreshToken)
                        .then((data) => {
                            persistTokens(data);
                            // Retry the original request with the new token
                            const retryOptions = {
                                ...options,
                                token: `Bearer ${data.token}`,
                            };
                            return innerInstance(endpoint, retryOptions);
                        })
                        .catch((error) => {
                            // Refresh failed - redirect to logout
                            goLogout();
                            return Promise.reject(error);
                        });
                }
                if (statusCategory === RESPONSE_STATUS.BAD_REQUEST) {
                    return Promise.reject(response);
                }
                return response;
            })
            .catch((error) => {
                console.error('Fetch error:', error);
                throw error; // Allow error handling at the caller level
            });
    };

    /**
     * Gets the base URL configured for this instance
     * @returns {string} The base URL
     */
    innerInstance.getBaseURL = () => config.baseURL;

    return innerInstance;
};

/**
 * Default fetch instance configured with the application's API URL
 * Provides automatic token management, error handling, and retry logic
 */
const instance = createInstance({
    baseURL: NEW_API_URL,
});

export const fetchInstance = instance;
